import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';

const PerformanceAnalytics = () => {
  const [sortField, setSortField] = useState('revenue');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedVertical, setSelectedVertical] = useState('all');

  const partnerPerformanceData = [
    {
      id: 1,
      name: 'Sarah Johnson',
      businessName: "Johnson\'s Catering Services",
      avatar: 'https://images.unsplash.com/photo-1684262855358-88f296a2cfc2',
      avatarAlt:
        'Professional headshot of woman with brown hair in white blazer smiling',
      vertical: 'Food',
      orders: 1247,
      revenue: 45680,
      rating: 4.8,
      completionRate: 98.5,
      responseTime: 12,
      tier: 'Gold',
      joinDate: '2024-03-15',
      lastActive: '2025-10-27T12:30:00Z',
    },
    {
      id: 2,
      name: 'Michael Chen',
      businessName: 'Chen Home Services',
      avatar: 'https://images.unsplash.com/photo-1659323593305-ad05297db6a2',
      avatarAlt:
        'Professional headshot of Asian man in navy suit with confident smile',
      vertical: 'Services',
      orders: 892,
      revenue: 32450,
      rating: 4.6,
      completionRate: 95.2,
      responseTime: 18,
      tier: 'Silver',
      joinDate: '2024-05-20',
      lastActive: '2025-10-27T11:45:00Z',
    },
    {
      id: 3,
      name: 'Emma Rodriguez',
      businessName: 'Rodriguez Property Management',
      avatar: 'https://images.unsplash.com/photo-1563220599-bc8cb877de5d',
      avatarAlt:
        'Professional headshot of Hispanic woman with long dark hair in business attire',
      vertical: 'Property',
      orders: 234,
      revenue: 78900,
      rating: 4.9,
      completionRate: 100,
      responseTime: 8,
      tier: 'Platinum',
      joinDate: '2024-01-10',
      lastActive: '2025-10-27T13:15:00Z',
    },
    {
      id: 4,
      name: 'David Thompson',
      businessName: 'Adventure Tours Co.',
      avatar: 'https://images.unsplash.com/photo-1598226863630-2826f1d31532',
      avatarAlt:
        'Professional headshot of man with beard in casual blue shirt outdoors',
      vertical: 'Tours',
      orders: 567,
      revenue: 23400,
      rating: 4.7,
      completionRate: 92.8,
      responseTime: 25,
      tier: 'Bronze',
      joinDate: '2024-07-08',
      lastActive: '2025-10-27T10:20:00Z',
    },
    {
      id: 5,
      name: 'Lisa Wang',
      businessName: "Wang\'s Delivery Express",
      avatar: 'https://images.unsplash.com/photo-1632720230739-79fa42d3dc34',
      avatarAlt:
        'Professional headshot of Asian woman with short black hair in business suit',
      vertical: 'Food',
      orders: 1890,
      revenue: 56780,
      rating: 4.5,
      completionRate: 94.1,
      responseTime: 15,
      tier: 'Gold',
      joinDate: '2024-02-28',
      lastActive: '2025-10-27T12:50:00Z',
    },
  ];

  const tierDistributionData = [
    { name: 'Platinum', value: 12, color: '#8B5CF6' },
    { name: 'Gold', value: 34, color: '#F59E0B' },
    { name: 'Silver', value: 56, color: '#6B7280' },
    { name: 'Bronze', value: 28, color: '#CD7F32' },
  ];

  const monthlyPerformanceData = [
    { month: 'Jan', orders: 2340, revenue: 145000 },
    { month: 'Feb', orders: 2890, revenue: 167000 },
    { month: 'Mar', orders: 3120, revenue: 189000 },
    { month: 'Apr', orders: 2980, revenue: 178000 },
    { month: 'May', orders: 3450, revenue: 203000 },
    { month: 'Jun', orders: 3780, revenue: 234000 },
  ];

  const verticalOptions = [
    { value: 'all', label: 'All Verticals' },
    { value: 'Food', label: 'Food' },
    { value: 'Services', label: 'Services' },
    { value: 'Property', label: 'Property' },
    { value: 'Tours', label: 'Tours' },
  ];

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Platinum':
        return 'bg-purple-100 text-purple-800';
      case 'Gold':
        return 'bg-yellow-100 text-yellow-800';
      case 'Silver':
        return 'bg-gray-100 text-gray-800';
      case 'Bronze':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerticalColor = (vertical) => {
    switch (vertical) {
      case 'Food':
        return 'bg-orange-100 text-orange-800';
      case 'Services':
        return 'bg-blue-100 text-blue-800';
      case 'Property':
        return 'bg-purple-100 text-purple-800';
      case 'Tours':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    })?.format(amount);
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const lastActive = new Date(dateString);
    const diffInMinutes = Math.floor((now - lastActive) / (1000 * 60));

    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredAndSortedData = partnerPerformanceData
    ?.filter(
      (partner) =>
        selectedVertical === 'all' || partner?.vertical === selectedVertical,
    )
    ?.sort((a, b) => {
      const aValue = a?.[sortField];
      const bValue = b?.[sortField];
      const multiplier = sortDirection === 'asc' ? 1 : -1;

      if (typeof aValue === 'string') {
        return aValue?.localeCompare(bValue) * multiplier;
      }
      return (aValue - bValue) * multiplier;
    });

  return (
    <div className='space-y-6'>
      {/* Performance Overview Cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-muted-foreground'>
                Total Partners
              </p>
              <p className='text-2xl font-bold text-foreground'>1,247</p>
              <p className='text-xs text-success'>+12% from last month</p>
            </div>
            <div className='w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center'>
              <Icon name='Users' size={24} className='text-blue-600' />
            </div>
          </div>
        </div>

        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-muted-foreground'>
                Avg Rating
              </p>
              <p className='text-2xl font-bold text-foreground'>4.7</p>
              <p className='text-xs text-success'>+0.2 from last month</p>
            </div>
            <div className='w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center'>
              <Icon name='Star' size={24} className='text-yellow-600' />
            </div>
          </div>
        </div>

        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-muted-foreground'>
                Completion Rate
              </p>
              <p className='text-2xl font-bold text-foreground'>96.2%</p>
              <p className='text-xs text-success'>+1.5% from last month</p>
            </div>
            <div className='w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center'>
              <Icon name='CheckCircle' size={24} className='text-green-600' />
            </div>
          </div>
        </div>

        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-muted-foreground'>
                Total Revenue
              </p>
              <p className='text-2xl font-bold text-foreground'>$2.4M</p>
              <p className='text-xs text-success'>+18% from last month</p>
            </div>
            <div className='w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center'>
              <Icon name='IndianRupee' size={24} className='text-purple-600' />
            </div>
          </div>
        </div>
      </div>
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Partner Performance Table */}
        <div className='lg:col-span-2 bg-card border border-border rounded-lg'>
          <div className='p-6 border-b border-border'>
            <div className='flex items-center justify-between'>
              <h3 className='text-lg font-semibold text-foreground'>
                Partner Performance
              </h3>
              <div className='flex items-center space-x-3'>
                <Select
                  options={verticalOptions}
                  value={selectedVertical}
                  onChange={setSelectedVertical}
                  placeholder='Filter by vertical'
                  className='w-40'
                />

                <Button variant='outline' size='sm'>
                  <Icon name='Download' size={16} className='mr-2' />
                  Export
                </Button>
              </div>
            </div>
          </div>

          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-muted'>
                <tr>
                  <th className='text-left p-4 font-medium text-muted-foreground'>
                    Partner
                  </th>
                  <th
                    className='text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground'
                    onClick={() => handleSort('orders')}
                  >
                    <div className='flex items-center space-x-1'>
                      <span>Orders</span>
                      <Icon name='ArrowUpDown' size={14} />
                    </div>
                  </th>
                  <th
                    className='text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground'
                    onClick={() => handleSort('revenue')}
                  >
                    <div className='flex items-center space-x-1'>
                      <span>Revenue</span>
                      <Icon name='ArrowUpDown' size={14} />
                    </div>
                  </th>
                  <th
                    className='text-left p-4 font-medium text-muted-foreground cursor-pointer hover:text-foreground'
                    onClick={() => handleSort('rating')}
                  >
                    <div className='flex items-center space-x-1'>
                      <span>Rating</span>
                      <Icon name='ArrowUpDown' size={14} />
                    </div>
                  </th>
                  <th className='text-left p-4 font-medium text-muted-foreground'>
                    Completion
                  </th>
                  <th className='text-left p-4 font-medium text-muted-foreground'>
                    Tier
                  </th>
                  <th className='text-left p-4 font-medium text-muted-foreground'>
                    Last Active
                  </th>
                  <th className='text-left p-4 font-medium text-muted-foreground'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedData?.map((partner) => (
                  <tr
                    key={partner?.id}
                    className='border-b border-border hover:bg-muted/50'
                  >
                    <td className='p-4'>
                      <div className='flex items-center space-x-3'>
                        <Image
                          src={partner?.avatar}
                          alt={partner?.avatarAlt}
                          className='w-10 h-10 rounded-full object-cover'
                        />

                        <div>
                          <p className='font-medium text-foreground'>
                            {partner?.name}
                          </p>
                          <p className='text-sm text-muted-foreground'>
                            {partner?.businessName}
                          </p>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${getVerticalColor(partner?.vertical)}`}
                          >
                            {partner?.vertical}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className='p-4 text-foreground font-medium'>
                      {partner?.orders?.toLocaleString()}
                    </td>
                    <td className='p-4 text-foreground font-medium'>
                      {formatCurrency(partner?.revenue)}
                    </td>
                    <td className='p-4'>
                      <div className='flex items-center space-x-1'>
                        <Icon
                          name='Star'
                          size={14}
                          className='text-yellow-500 fill-current'
                        />
                        <span className='text-foreground font-medium'>
                          {partner?.rating}
                        </span>
                      </div>
                    </td>
                    <td className='p-4'>
                      <div className='flex items-center space-x-2'>
                        <div className='w-16 bg-muted rounded-full h-2'>
                          <div
                            className='bg-success h-2 rounded-full'
                            style={{ width: `${partner?.completionRate}%` }}
                          ></div>
                        </div>
                        <span className='text-sm text-foreground'>
                          {partner?.completionRate}%
                        </span>
                      </div>
                    </td>
                    <td className='p-4'>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${getTierColor(partner?.tier)}`}
                      >
                        {partner?.tier}
                      </span>
                    </td>
                    <td className='p-4 text-sm text-muted-foreground'>
                      {formatTimeAgo(partner?.lastActive)}
                    </td>
                    <td className='p-4'>
                      <div className='flex items-center space-x-2'>
                        <Button variant='ghost' size='sm'>
                          <Icon name='Eye' size={16} />
                        </Button>
                        <Button variant='ghost' size='sm'>
                          <Icon name='MessageSquare' size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Charts Section */}
        <div className='space-y-6'>
          {/* Tier Distribution */}
          <div className='bg-card border border-border rounded-lg p-6'>
            <h3 className='text-lg font-semibold text-foreground mb-4'>
              Partner Tier Distribution
            </h3>
            <div className='h-64'>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <Pie
                    data={tierDistributionData}
                    cx='50%'
                    cy='50%'
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey='value'
                  >
                    {tierDistributionData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className='grid grid-cols-2 gap-2 mt-4'>
              {tierDistributionData?.map((tier) => (
                <div key={tier?.name} className='flex items-center space-x-2'>
                  <div
                    className='w-3 h-3 rounded-full'
                    style={{ backgroundColor: tier?.color }}
                  ></div>
                  <span className='text-sm text-muted-foreground'>
                    {tier?.name}: {tier?.value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Performance */}
          <div className='bg-card border border-border rounded-lg p-6'>
            <h3 className='text-lg font-semibold text-foreground mb-4'>
              Monthly Performance
            </h3>
            <div className='h-64'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={monthlyPerformanceData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='month' />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey='orders' fill='var(--color-primary)' />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceAnalytics;
